import { Loader } from './Loader.js'
import { Camera, CameraAttribute } from './objects/Camera.js'
import { Rectangle } from './objects/Rectangle.js'
import { Ellipse } from './objects/Ellipse.js'
import { Text } from './objects/Text.js'
import { LeviarObject } from './LeviarObject.js'
import { LeviarImage } from './objects/LeviarImage.js'
import { LeviarVideo } from './objects/LeviarVideo.js'
import { Sprite } from './objects/Sprite.js'
import { Particle } from './objects/Particle.js'
import type { ParticleOptions } from './objects/Particle.js'
import { SpriteManager } from './SpriteManager.js'
import { VideoManager } from './VideoManager.js'
import { ParticleManager } from './ParticleManager.js'
import { PhysicsEngine } from './PhysicsEngine.js'
import type { LeviarObjectOptions, LoadedAssets, Attribute, WorldEvents } from './types.js'
import type { RectangleOptions } from './objects/Rectangle.js'
import { Renderer } from './Renderer.js'
import { EventEmitter } from './EventEmitter.js'
import { Mat4, Vec3 as OglVec3 } from 'ogl'

const AXIS_X = new OglVec3(1, 0, 0)
const AXIS_Y = new OglVec3(0, 1, 0)
const AXIS_Z = new OglVec3(0, 0, 1)

function clampSize(value: number, min: number | undefined, max: number | undefined): number {
  let result = value
  if (min !== undefined) result = Math.max(result, min)
  if (max !== undefined) result = Math.min(result, max)
  return result
}

export interface WorldOptions {
  /** 캔버스 엘리먼트. 지정하지 않으면 자동으로 생성합니다. */
  canvas?: HTMLCanvasElement

  /**
   * 브라우저 기본 우클릭 메뉴를 막을지 여부.
   * true로 설정해도 LeviarObject의 'contextmenu' 이벤트는 정상적으로 수신됩니다.
   * @default true
   */
  disableContextMenu?: boolean
}

/**
 * MouseEvent를 래핑하여 stopPropagation() 호출 여부를 추적합니다.
 */
function wrapMouseEvent(e: MouseEvent): MouseEvent & { _propagationStopped: boolean } {
  const wrapped = e as MouseEvent & { _propagationStopped: boolean }
  if (wrapped._propagationStopped !== undefined) return wrapped
  wrapped._propagationStopped = false
  const original = e.stopPropagation.bind(e)
  e.stopPropagation = () => {
    wrapped._propagationStopped = true
    original()
  }
  return wrapped
}

export class World extends EventEmitter<WorldEvents> {
  private renderer: Renderer
  private objects: Set<LeviarObject> = new Set()
  private rafId: number | null = null
  private physics: PhysicsEngine = new PhysicsEngine()
  private _canvas: HTMLCanvasElement | null = null
  /** 현재 포커스 중인 카메라 (지정되지 않으면 객체 중 Camera를 찾습니다) */
  private _activeCamera: Camera | null = null
  /** 물리 엔진의 중력 프로퍼티 프록시 */
  private _gravityProxy: { x: number; y: number }
  /** mouseover 상태 추적 (객체 id → boolean) */
  private _mouseOver: Set<string> = new Set()
  /** destroy 호출 여부 — true이면 모든 공개 메서드가 즉시 반환됩니다 */
  private _destroyed: boolean = false
  /** 캔버스에 등록된 마우스 이벤트 핸들러 (destroy 시 제거용) */
  private _mouseEventHandlers: Array<{ type: string; handler: EventListener }> = []

  /** 브라우저 기본 컨텍스트 메뉴 비활성화 여부 */
  public disableContextMenu: boolean

  /** 스프라이트 애니메이션 클립 매니저 */
  readonly spriteManager: SpriteManager = new SpriteManager()
  /** 비디오 클립 매니저 */
  readonly videoManager: VideoManager = new VideoManager()
  /** 파티클 클립 매니저 */
  readonly particleManager: ParticleManager = new ParticleManager()
  /** 에셋 로더 */
  readonly loader: Loader

  /** 모든 Loader에서 로드된 에셋의 통합 맵 */
  private _assets: LoadedAssets = {}

  constructor(canvasOrOptions?: HTMLCanvasElement | WorldOptions) {
    super()
    let canvasEl: HTMLCanvasElement
    let options: WorldOptions = {}

    if (canvasOrOptions instanceof HTMLCanvasElement) {
      canvasEl = canvasOrOptions
    } else {
      options = canvasOrOptions ?? {}
      canvasEl = options.canvas ?? this.createCanvas()
    }

    this.disableContextMenu = options.disableContextMenu ?? true
    this._canvas = canvasEl
    this.renderer = new Renderer(canvasEl)
    this.loader = new Loader()
    this.loader.on('complete', ({ assets }) => {
      Object.assign(this._assets, assets)
    })
    this._setupMouseEvents(canvasEl)

    this._gravityProxy = new Proxy({ x: 0, y: 0 } as { x: number; y: number }, {
      get: (_, prop: 'x' | 'y') => this.physics.engine.gravity[prop],
      set: (_, prop: 'x' | 'y', value: number) => {
        this.physics.engine.gravity[prop] = value
        return true
      }
    })
  }

  private createCanvas(): HTMLCanvasElement {
    const canvas = document.createElement('canvas')
    canvas.width = window.innerWidth
    canvas.height = window.innerHeight
    canvas.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;'
    document.body.appendChild(canvas)

    window.addEventListener('resize', () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    })

    return canvas
  }

  // ─── 마우스 이벤트 ────────────────────────────────

  /**
   * 캔버스상의 정확한 내부 마우스 좌표를 계산합니다. (object-fit: contain 보정 포함)
   */
  private _getCanvasMousePos(e: MouseEvent): { x: number; y: number } {
    const canvas = this._canvas!
    const rect = canvas.getBoundingClientRect()
    let cssWidth = rect.width
    let cssHeight = rect.height
    let offsetX = 0
    let offsetY = 0

    const objectFit = getComputedStyle(canvas).objectFit
    if (objectFit === 'contain') {
      const cssRatio = cssWidth / cssHeight
      const canvasRatio = canvas.width / canvas.height
      // 오차가 생기지 않도록 소수점 처리 방지 및 정확한 비율 계산
      if (cssRatio > canvasRatio) {
        cssWidth = cssHeight * canvasRatio
        offsetX = (rect.width - cssWidth) / 2
      } else if (cssRatio < canvasRatio) {
        cssHeight = cssWidth / canvasRatio
        offsetY = (rect.height - cssHeight) / 2
      }
    }

    const scaleX = canvas.width / cssWidth
    const scaleY = canvas.height / cssHeight

    return {
      x: (e.clientX - rect.left - offsetX) * scaleX,
      y: (e.clientY - rect.top - offsetY) * scaleY
    }
  }

  private _setupMouseEvents(canvas: HTMLCanvasElement) {
    // 객체에 이벤트를 emit하고, 전파가 막히지 않으면 world로 버블링합니다.
    const dispatch = (eventName: string, e: MouseEvent) => {
      const wrapped = wrapMouseEvent(e)
      const hits = this._getHitObjects(wrapped)

      // 디버그 모드: 클릭한 오브젝트 정보를 console에 출력합니다.
      if (this.debugMode && eventName === 'click') {
        if (hits.length > 0) {
          console.log('[Leviar Debug] Clicked:', hits[0])
          if (hits.length > 1) {
            console.log('[Leviar Debug] All hit objects:', hits)
          }
          // hit-area 강조 (fade-out)
          const now = performance.now()
          for (const obj of hits) {
            this.renderer.debugClickedIds.set(obj.attribute.id, now)
          }
        } else {
          console.log('[Leviar Debug] Click: no object hit')
        }
      }

      for (const obj of hits) {
        obj.emit(eventName, wrapped)
        if (wrapped._propagationStopped) return
      }
      // 전파가 막히지 않으면 world로 버블링
      this.emit(eventName, hits[0], wrapped)
    }

    const addTracked = (type: string, handler: EventListener) => {
      canvas.addEventListener(type, handler)
      this._mouseEventHandlers.push({ type, handler })
    }

    addTracked('click', ((e: MouseEvent) => {
      if (this.debugMode) {
        const pos = this._getCanvasMousePos(e)
        this.renderer.debugRipples.push({
          x: pos.x,
          y: pos.y,
          time: performance.now(),
        })
      }
      dispatch('click', e)
    }) as EventListener)
    addTracked('dblclick', ((e: MouseEvent) => dispatch('dblclick', e)) as EventListener)
    addTracked('contextmenu', ((e: MouseEvent) => {
      if (this.disableContextMenu) {
        e.preventDefault()
      }
      dispatch('contextmenu', e)
    }) as EventListener)
    addTracked('mousedown', ((e: MouseEvent) => dispatch('mousedown', e)) as EventListener)
    addTracked('mouseup', ((e: MouseEvent) => dispatch('mouseup', e)) as EventListener)

    addTracked('mousemove', ((e: MouseEvent) => {
      const wrapped = wrapMouseEvent(e)
      const hits = this._getHitObjects(wrapped)
      const hitIds = new Set(hits.map(o => o.attribute.id))

      // 디버그: hover 상태 동기화
      if (this.debugMode) {
        this.renderer.debugHoveredIds = hitIds
      }

      // cursor: 가장 위 hit 객체의 style.cursor를 canvas에 반영
      const topCursor = hits.find(o => o.style.cursor)?.style.cursor
      canvas.style.cursor = topCursor ?? ''

      // mouseover: 새로 진입한 객체
      for (const obj of hits) {
        if (!this._mouseOver.has(obj.attribute.id)) {
          this._mouseOver.add(obj.attribute.id)
          obj.emit('mouseover', wrapped)
          if (!wrapped._propagationStopped) this.emit('mouseover', obj, wrapped)
        }
        obj.emit('mousemove', wrapped)
        if (!wrapped._propagationStopped) this.emit('mousemove', obj, wrapped)
      }

      // mouseout: 이전에 over였는데 이번에 없는 객체
      for (const id of Array.from(this._mouseOver)) {
        if (!hitIds.has(id)) {
          this._mouseOver.delete(id)
          const obj = Array.from(this.objects).find(o => o.attribute.id === id)
          if (obj) {
            obj.emit('mouseout', wrapped)
            if (!wrapped._propagationStopped) this.emit('mouseout', obj, wrapped)
          }
        }
      }
    }) as EventListener)

    addTracked('mouseleave', ((e: MouseEvent) => {
      canvas.style.cursor = ''
      const wrapped = wrapMouseEvent(e)
      for (const id of Array.from(this._mouseOver)) {
        const obj = Array.from(this.objects).find(o => o.attribute.id === id)
        if (obj) {
          obj.emit('mouseout', wrapped)
          if (!wrapped._propagationStopped) this.emit('mouseout', obj, wrapped)
        }
      }
      this._mouseOver.clear()
    }) as EventListener)
  }

  /**
   * 화면좌표 기준으로 마우스 위치에 겹쳐지는 객체를 반환합니다. (AABB hit-test)
   */
  private _getHitObjects(e: MouseEvent): LeviarObject[] {
    const canvas = this._canvas
    if (!canvas) return []

    // 캔버스 상대 마우스 좌표 (DPR 및 object-fit: contain 고려)
    const pos = this._getCanvasMousePos(e)
    const mouseX = pos.x - canvas.width / 2
    const mouseY = -(pos.y - canvas.height / 2)

    // 카메라 위치 및 회전
    let camX = 0, camY = 0, camZ = 0
    let camRotX = 0, camRotY = 0, camRotZ = 0
    const activeCam = this.camera
    if (activeCam) {
      camX = activeCam.transform.position.x
      camY = activeCam.transform.position.y
      camZ = activeCam.transform.position.z
      camRotX = activeCam.transform.rotation.x || 0
      camRotY = activeCam.transform.rotation.y || 0
      camRotZ = activeCam.transform.rotation.z || 0
    }

    const radX = -camRotX * Math.PI / 180
    const radY = -camRotY * Math.PI / 180
    const radZ = -camRotZ * Math.PI / 180

    const focalLength = activeCam ? (activeCam as Camera).attribute.focalLength ?? 100 : 100
    const result: LeviarObject[] = []
    const modelMat = new Mat4()

    const pointInPoly = (px: number, py: number, poly: { x: number, y: number }[]) => {
      let inside = false
      for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const xi = poly[i].x, yi = poly[i].y
        const xj = poly[j].x, yj = poly[j].y
        const intersect = ((yi > py) !== (yj > py))
          && (px < (xj - xi) * (py - yi) / (yj - yi) + xi)
        if (intersect) inside = !inside
      }
      return inside
    }

    // z 기준 내림차순 정렬을 위해 모든 변환 사전 계산 (상단 객체 우선 판정)
    // OGL 행렬은 열우선이므로 obj.__worldMatrix의 [12, 13, 14]가 월드 좌표(x, y, z)입니다.
    const objectsData = Array.from(this.objects)
      .filter(obj => obj.attribute.type !== 'camera' && obj.__worldDisplay !== 'none' && obj.__worldOpacity > 0 && obj.style.pointerEvents)
      .map(obj => {
        const mat = obj.__worldMatrix as unknown as Float32Array
        // Leviar와 OpenGL 좌표계 연결 과정에서 역방향된 z를 본래 양수 스케일로 복원 (-1 곱함)
        const wx = mat[12], wy = mat[13], wz = -mat[14]

        let dx = wx - camX
        let dy = wy - camY
        let dz = wz - camZ

        if (radY !== 0) {
          const cosY = Math.cos(radY), sinY = Math.sin(radY)
          const nx = dx * cosY + dz * sinY
          const nz = -dx * sinY + dz * cosY
          dx = nx; dz = nz
        }
        if (radX !== 0) {
          const cosX = Math.cos(radX), sinX = Math.sin(radX)
          const ny = dy * cosX - dz * sinX
          const nz = dy * sinX + dz * cosX
          dy = ny; dz = nz
        }
        if (radZ !== 0) {
          const cosZ = Math.cos(radZ), sinZ = Math.sin(radZ)
          const nx = dx * cosZ - dy * sinZ
          const ny = dx * sinZ + dy * cosZ
          dx = nx; dy = ny
        }
        return { obj, dx, dy, dz }
      })
      .filter(data => data.dz >= 0)
      .filter(data => {
        // 1차 고속 필터링: Bounding Box (AABB) 기반 Early Exit
        const perspectiveScale = data.dz === 0 ? 1 : focalLength / data.dz
        const screenX = data.dx * perspectiveScale
        const screenY = data.dy * perspectiveScale

        // 실제 크기가 아직 0이면(예: 이미지 로드 전) 무조건 패스
        const rawBaseW = data.obj.__renderedSize?.w ?? data.obj.style.width ?? 100
        const rawBaseH = data.obj.__renderedSize?.h ?? data.obj.style.height ?? 100
        const baseW = clampSize(rawBaseW, data.obj.style.minWidth, data.obj.style.maxWidth)
        const baseH = clampSize(rawBaseH, data.obj.style.minHeight, data.obj.style.maxHeight)
        const w = baseW * perspectiveScale * Math.abs(data.obj.transform.scale.x)
        const h = baseH * perspectiveScale * Math.abs(data.obj.transform.scale.y)

        // 회전 및 피벗을 모두 커버하는 넉넉한 충돌 안전 반경 (대각선 근사치 w + h)
        // 화면 위치(screenX, screenY)에서 마우스가 이 반경을 벗어나면 절대 충돌할 수 없음
        const safeRadius = w + h
        if (Math.abs(mouseX - screenX) > safeRadius || Math.abs(mouseY - screenY) > safeRadius) {
          return false
        }
        return true
      })
      .sort((a, b) => {
        const zdiff = b.dz - a.dz
        return zdiff !== 0 ? zdiff : a.obj.style.zIndex - b.obj.style.zIndex
      })

    for (const { obj, dx, dy, dz } of objectsData) {
      const { transform, style } = obj
      const perspectiveScale = dz === 0 ? 1 : focalLength / dz

      const screenX = dx * perspectiveScale
      const screenY = dy * perspectiveScale

      const rawBaseW = obj.__renderedSize?.w ?? style.width ?? 0
      const rawBaseH = obj.__renderedSize?.h ?? style.height ?? 0
      const baseW = clampSize(rawBaseW, style.minWidth, style.maxWidth)
      const baseH = clampSize(rawBaseH, style.minHeight, style.maxHeight)
      const w = baseW * perspectiveScale * transform.scale.x
      const h = baseH * perspectiveScale * transform.scale.y

      if (w <= 0 || h <= 0) continue

      modelMat.identity()
      modelMat.translate(new OglVec3(screenX, screenY, 0))

      if (camRotY) modelMat.rotate(-camRotY * Math.PI / 180, AXIS_Y)
      if (camRotX) modelMat.rotate(-camRotX * Math.PI / 180, AXIS_X)
      if (camRotZ) modelMat.rotate(-camRotZ * Math.PI / 180, AXIS_Z)

      const rot = transform.rotation
      if (rot.z) modelMat.rotate(rot.z * Math.PI / 180, AXIS_Z)
      if (rot.y) modelMat.rotate(rot.y * Math.PI / 180, AXIS_Y)
      if (rot.x) modelMat.rotate(rot.x * Math.PI / 180, AXIS_X)

      const pivot = transform.pivot
      modelMat.translate(new OglVec3(
        (0.5 - pivot.x) * w,
        -(0.5 - pivot.y) * h,
        0
      ))
      modelMat.scale(new OglVec3(w, h, 1))

      const m = modelMat as unknown as Float32Array
      // Quad 꼭짓점 투영 (-0.5 ~ +0.5)
      const getPt = (px: number, py: number) => ({
        x: m[0] * px + m[4] * py + m[12],
        y: m[1] * px + m[5] * py + m[13]
      })
      const corners = [
        getPt(-0.5, -0.5), getPt(0.5, -0.5),
        getPt(0.5, 0.5), getPt(-0.5, 0.5)
      ]

      if (pointInPoly(mouseX, mouseY, corners)) {
        result.push(obj)
      }
    }

    return result
  }

  /**
   * 월드에 연결된 캔버스 엘리먼트를 반환합니다.
   */
  get canvas(): HTMLCanvasElement | null {
    return this._canvas
  }

  /**
   * 월드의 중력 x, y 값. 직접 수정하거나 새 객체를 할당할 수 있습니다.
   */
  get gravity(): { x: number; y: number } {
    return this._gravityProxy
  }

  set gravity(g: { x: number; y: number }) {
    this.physics.engine.gravity.x = g.x
    this.physics.engine.gravity.y = g.y
  }

  /**
   * 월드의 활성 카메라 객체를 반환합니다. 
   */
  get camera(): Camera | null {
    return this._activeCamera
  }

  /**
   * 월드의 카메라를 특정 객체로 지정합니다. 카메라 객체만 지정할 수 있습니다.
   * `null`을 할당하면 기본 동작으로 돌아갑니다.
   */
  set camera(camera: Camera | null) {
    if (camera != null && camera.attribute.type !== 'camera') {
      throw new Error('The assigned object must be of camera type.');
    }
    this._activeCamera = camera
  }

  /**
   * 디버그 모드 활성화 여부를 반환합니다.
   */
  get debugMode(): boolean {
    return this.renderer.debugMode
  }

  /**
   * 디버그 모드를 활성화하거나 비활성화합니다.
   * 활성화 시 각 오브젝트의 실제 렌더 경계(outline)와 margin 영역이
   * 기존 스타일을 건드리지 않고 별도 레이어로 시각화됩니다.
   */
  set debugMode(value: boolean) {
    this.renderer.debugMode = value
  }

  /**
   * CSS querySelector와 유사한 방식으로 오브젝트를 선택합니다.
   * 지원 셀렉터: `.className`, `#id`, `[attribute=value]`
   */
  select(query: string): LeviarObject[] {
    const all = Array.from(this.objects)

    const classMatches = query.match(/\.([a-zA-Z0-9_-]+)/g)
    const classes = classMatches ? classMatches.map(c => c.slice(1)) : []

    const attrRegex = /\[([a-zA-Z0-9_-]+)=([^\]]+)\]/g
    const attrConditions: { key: string, parsedValue: any, rawValue: string }[] = []

    let match
    while ((match = attrRegex.exec(query)) !== null) {
      const key = match[1].trim()
      const rawValue = match[2].trim()
      let parsedValue: any = rawValue

      if (
        (rawValue.startsWith('"') && rawValue.endsWith('"')) ||
        (rawValue.startsWith("'") && rawValue.endsWith("'"))
      ) {
        parsedValue = rawValue.slice(1, -1)
      } else if (rawValue === 'true') {
        parsedValue = true
      } else if (rawValue === 'false') {
        parsedValue = false
      } else if (rawValue === 'null') {
        parsedValue = null
      } else if (!Number.isNaN(Number(rawValue)) && rawValue !== '') {
        parsedValue = Number(rawValue)
      }

      attrConditions.push({ key, parsedValue, rawValue })
    }

    if (classes.length === 0 && attrConditions.length === 0) {
      return []
    }

    return all.filter(o => {
      if (classes.length > 0) {
        const objClasses = (o.attribute.className || '').split(/\s+/).filter(Boolean)
        const hasAllClasses = classes.every(c => objClasses.includes(c))
        if (!hasAllClasses) return false
      }

      for (const cond of attrConditions) {
        const { key, parsedValue, rawValue } = cond

        if (key.startsWith('data-')) {
          const dataKey = key.slice(5)
          if ((o.dataset as any)[dataKey] !== parsedValue) return false
        } else if (key.startsWith('attr-')) {
          const attrKey = key.slice(5)
          const objVal = (o.attribute as any)[attrKey]
          if (objVal !== parsedValue && String(objVal) !== rawValue) return false
        } else {
          return false
        }
      }

      return true
    })
  }

  /**
   * 에셋 로더를 생성합니다. 로드 완료 시 World 내부 에셋 맵에 자동으로 병합됩니다.
   * @deprecated world.loader를 직접 사용하십시오.
   */
  createLoader(): Loader {
    return this.loader
  }



  // ─── Object 등록 ─────────────────────────────────────────

  private _registerObject(obj: LeviarObject) {
    this.objects.add(obj)
    obj.on('remove', (target: LeviarObject) => {
      this.removeObject(target)
    })
  }

  // ─── Object 생성 ─────────────────────────────────────────

  createCamera<D extends Record<string, any> = Record<string, any>>(options?: LeviarObjectOptions<CameraAttribute, D>): Camera<D> {
    const cam = new Camera<D>(options)
    cam.__world = this
    if (options?.transform?.position?.z === undefined) {
      cam.transform.position.z = -(cam.attribute.focalLength ?? 100)
    }
    this._registerObject(cam)
    this._tryAddPhysics(cam)
    this.renderer.markSortDirty()
    return cam
  }

  createRectangle<D extends Record<string, any> = Record<string, any>>(options?: RectangleOptions<D>): Rectangle<D> {
    const rect = new Rectangle<D>(options)
    this._registerObject(rect)
    this._tryAddPhysics(rect, options?.style?.width, options?.style?.height)
    this._trackSortDirty(rect)
    this.renderer.markSortDirty()
    return rect
  }

  createEllipse<D extends Record<string, any> = Record<string, any>>(options?: LeviarObjectOptions<Record<string, any>, D>): Ellipse<D> {
    const el = new Ellipse<D>(options)
    this._registerObject(el)
    this._tryAddPhysics(el, options?.style?.width, options?.style?.height)
    this._trackSortDirty(el)
    this.renderer.markSortDirty()
    return el
  }

  createText<D extends Record<string, any> = Record<string, any>>(options?: LeviarObjectOptions<Record<string, any>, D>): Text<D> {
    const text = new Text<D>(options)
    this._registerObject(text)
    this._trackSortDirty(text)
    this.renderer.markSortDirty()
    return text
  }

  createImage<D extends Record<string, any> = Record<string, any>>(options?: LeviarObjectOptions<Record<string, any>, D>): LeviarImage<D> {
    const img = new LeviarImage<D>(options)
    this._registerObject(img)
    this._trackSortDirty(img)
    this.renderer.markSortDirty()
    return img
  }

  createVideo<D extends Record<string, any> = Record<string, any>>(options?: LeviarObjectOptions<Record<string, any>, D>): LeviarVideo<D> {
    const video = new LeviarVideo<D>(options)
    video.__setManager(this.videoManager)
    this._registerObject(video)
    this._trackSortDirty(video)
    this.renderer.markSortDirty()
    return video
  }

  createSprite<D extends Record<string, any> = Record<string, any>>(options?: LeviarObjectOptions<Record<string, any>, D>): Sprite<D> {
    const sprite = new Sprite<D>(options)
    sprite.__setManager(this.spriteManager)
    this._registerObject(sprite)
    this._trackSortDirty(sprite)
    this.renderer.markSortDirty()
    return sprite
  }

  createParticle<D extends Record<string, any> = Record<string, any>>(options?: ParticleOptions<D>): Particle<D> {
    const particle = new Particle<D>(options)
    particle.__setPhysics(this.physics)
    particle.__setManager(this.particleManager)
    this._registerObject(particle)
    this._trackSortDirty(particle)
    this.renderer.markSortDirty()
    return particle
  }

  removeObject(obj: LeviarObject) {
    this.physics.removeBody(obj)
    this.objects.delete(obj)
    this.renderer.removeTextEntry(obj.attribute.id)
    this.renderer.markSortDirty()
  }

  /**
   * 렌더 루프를 시작합니다. 파괴된 월드에서는 아무것도 하지 않습니다.
   * @returns this
   */
  start(): this {
    if (this.rafId != null) return this
    let prevTime = 0

    const loop = (timestamp: number) => {
      if (prevTime !== 0) {
        this.physics.step(timestamp)
      }
      prevTime = timestamp

      // 렌더링 전 모든 루트 객체(부모 없는 객체)와 카메라의 월드 매트릭스를 계층 구조에 맞게 최신화
      for (const obj of this.objects) {
        if (!obj.parent) {
          obj.__updateMatrixWorld()
        }
      }

      this.renderer.render(this.objects, this._assets, timestamp, this.camera)
      // 렌더 후 실제 크기가 확정되면 물리 바디 크기를 동기화
      this.physics.syncObjectSizes(this.objects)
      this.emit('update', timestamp)
      this.rafId = requestAnimationFrame(loop)
    }

    this.rafId = requestAnimationFrame(loop)
    return this
  }

  /**
   * 렌더 루프를 중지합니다. 재시작은 start() 호출로 가능합니다.
   * @returns this
   */
  stop(): this {
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
    return this
  }

  /**
   * 월드가 파괴되었는지 여부를 반환합니다.
   */
  get destroyed(): boolean {
    return this._destroyed
  }

  /**
   * 월드를 완전히 파괴합니다.
   * 렌더 루프를 중지하고, 모든 비디오를 정지하며, 에셋의 Blob URL을 해제하고,
   * 물리 엔진·매니저·렌더러(WebGL 컨텍스트 포함)를 정리합니다.
   * 호출 후 이 World 인스턴스는 다시 사용할 수 없습니다.
   */
  destroy(): void {
    if (this._destroyed) return
    this._destroyed = true

    // 1. 렌더 루프 중지
    this.stop()

    // 2. 모든 비디오 정지 및 비디오 객체 정리
    for (const obj of this.objects) {
      if (obj instanceof LeviarVideo) {
        obj.stop()
        if (obj.__videoElement) {
          obj.__videoElement.pause()
          obj.__videoElement = null
        }
      }
    }

    // 3. 에셋의 Blob URL 해제 (Loader에서 createObjectURL로 생성한 것들)
    for (const asset of Object.values(this._assets)) {
      if (asset instanceof HTMLVideoElement) {
        const blobUrl = asset.src
        asset.pause()
        asset.removeAttribute('src')
        asset.load()
        if (blobUrl.startsWith('blob:')) {
          URL.revokeObjectURL(blobUrl)
        }
      }
    }
    this._assets = {}

    // 4. 물리 엔진에서 모든 바디 제거
    for (const obj of this.objects) {
      this.physics.removeBody(obj)
    }

    // 5. 오브젝트 Set 비우기
    this.objects.clear()

      // 6. 매니저 클립 비우기
      ; (this.spriteManager as any).clips?.clear?.()
      ; (this.videoManager as any).clips?.clear?.()
      ; (this.particleManager as any).clips?.clear?.()

    // 7. 렌더러 파괴 (WebGL 컨텍스트, 텍스처 캐시, 디버그 오버레이)
    this.renderer.destroy()

    // 8. 캔버스 이벤트 리스너 제거
    if (this._canvas) {
      for (const { type, handler } of this._mouseEventHandlers) {
        this._canvas.removeEventListener(type, handler)
      }
    }
    this._mouseEventHandlers.length = 0
    this._mouseOver.clear()

    // 9. 카메라 참조 해제
    this._activeCamera = null

    // 10. 이벤트 emit 후 리스너 전체 해제
    this.emit('destroy')
      ; (this as any).listeners?.clear?.()
  }

  /**
   * 객체의 Z 좌표 또는 zIndex 변경 시 Z-Sort 캐시를 무효화합니다.
   */
  private _trackSortDirty(obj: LeviarObject) {
    obj.on('positionmodified', (axis: string) => {
      if (axis === 'z') this.renderer.markSortDirty()
    })
    obj.on('cssmodified', (key: string) => {
      if (key === 'zIndex' || key === 'display' || key === 'transformStyle') this.renderer.markSortDirty()
    })
  }

  private _tryAddPhysics(obj: LeviarObject, w?: number, h?: number) {
    if (!obj.attribute.physics) return

    this.physics.addBody(obj, w ?? 32, h ?? 32)

    // 크기에 영향을 주는 속성 변경 시 물리 바디를 재생성합니다.
    const resizeBody = () => {
      const sw = clampSize(obj.style.width ?? w ?? 32, obj.style.minWidth, obj.style.maxWidth) * obj.transform.scale.x
      const sh = clampSize(obj.style.height ?? h ?? 32, obj.style.minHeight, obj.style.maxHeight) * obj.transform.scale.y
      this.physics.updateBodySize(obj, sw, sh)
    }

    const CSS_RESIZE_KEYS = new Set(['width', 'height', 'minWidth', 'maxWidth', 'minHeight', 'maxHeight', 'borderWidth', 'margin'])
    obj.on('cssmodified', (key) => {
      if (CSS_RESIZE_KEYS.has(key)) resizeBody()
    })
    obj.on('scalemodified', () => resizeBody())
  }
}
